<template>
  <div class="d-flex TemplatePicker">
    <folder-drawer v-if="areThereTemplatesInFolders && rootFolders.length"
                   class="mt-4">
    </folder-drawer>
    <div class="flex-grow-1 px-4"
         :class="{'ml-sm-2': areThereTemplatesInFolders && rootFolders.length }">

      <v-autocomplete v-if="templates.length"
                      v-model="selectedTemplate"
                      :items="templates"
                      item-value="id"
                      :item-text="templateNameSubject"
                      return-object
                      clearable
                      hide-details
                      full-width
                      dense
                      prepend-inner-icon="fal fa-search"
                      :label="rootFolders.length ? 'Search for an email template in any folder.' : 'Search for an email template.'"
                      outlined
                      placeholder="Search for a template by name or subject."
                      @change="selectTemplate"
                      class="mb-4">
        <template v-slot:item="{item: template}">
          <div class="d-block py-2">
            <div class="text-h6">
              Name: {{ template.name }}
            </div>
            <div class="text-body-1">
              Subject: {{ template.subject }}
            </div>
            <div v-if="template.is_english === false"
                 class="text-body-1">
              Language: {{ template.locale_name }}
            </div>
            <div v-if="template.expires_at"
                 class="text-body-1">
              Expires: {{ template.expires_at | datetime('MMMM D, YYYY', false) }}
            </div>
          </div>
        </template>
      </v-autocomplete>

      <v-list v-if="filteredTemplates.length"
              class="TemplatePicker__List pt-0">
        <template v-for="template in filteredTemplates">
          <v-list-item :key="template.id"
                       class="d-block d-sm-flex pl-1 pl-sm-2 ">
            <v-list-item-icon class="mr-2">
              <v-btn @click.prevent="select(template)"
                     class="mr-2"
                     small>
                Select
              </v-btn>

              <v-btn @click.prevent="preview(template)"
                     small>
                Preview
              </v-btn>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-if="template.is_featured">
                Name: {{ template.name }} (FEATURED)
              </v-list-item-title>
              <v-list-item-title v-else>
                Name: {{ template.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                Subject: <em>{{ template.subject }}</em>
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="template.is_english === false">
                Language: <em>{{ template.locale_name }}</em>
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="template.expires_at">
                Expires: <em>{{ template.expires_at | datetime('MMMM D, YYYY') }}</em>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </template>
      </v-list>
      <v-alert type="info"
               class="mt-4 ml-3"
               outlined
               text
               v-else>
        <div v-if="folder && folder.children.length">
          There are no templates in this folder. Select a different folder or subfolder on the left. Most templates are probably in the Home folder.
        </div>
        <div v-else-if="folder"
             class="ml-4">
          There are no templates in this folder. Select a folder on the left. Most templates are probably in the Home folder.
        </div>
        <div v-else-if="rootFolders && rootFolders.length"
             class="ml-4">
          There are no templates here. Select a folder on the left.
        </div>
        <div v-else
             class="ml-4">
          You don't have any templates.
        </div>
      </v-alert>
    </div>

    <v-dialog v-model="previewDialog"
              max-width="90%"
              scrollable>
      <v-card v-if="previewData && previewTemplate">
        <v-card-title class="primary white--text">
          Preview of {{ previewTemplate.name }}
          <v-spacer></v-spacer>
          <v-btn @click.prevent="previewDialog = false"
                 dark
                 text>
            Close
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-divider></v-divider>
          <div>
            From: {{ fromName }}
          </div>
          <div>
            Reply To: {{ user.reply_to_address ? user.reply_to_address : user.email }}
          </div>
          <div>
            Subject: {{ previewData.subject }}
          </div>
          <v-divider></v-divider>
        </v-card-text>
        <v-responsive v-if="dataURI"
                      class="mx-4 embed-responsive"
                      :aspect-ratio="4/3">
          <iframe :src="dataURI">
          </iframe>
        </v-responsive>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import FolderDrawer from '../../content/components/FolderDrawer';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import {isNull} from 'lodash';
import htmlService from '../../../services/htmlService';

export default {
  components: {FolderDrawer},
  props: {
    form: {
      type: Object,
      required: true,
    },
    templates: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      previewData: null,
      previewTemplate: null,
      previewDialog: false,
      selectedTemplate: null, // MLD-4666
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'folder',
      'homeFolder',
      'isHome',
      'rootFolders',
    ]),
    areThereTemplatesInFolders() { // MLD-3276
      const templatesInFolders = this.templates.filter(t => !isNull(t.folder_id));
      return templatesInFolders.length > 0;
    },
    filteredTemplates() {
      if (this.isHome) {
        return this.templates.filter(t => t.folder_id === null);
      }
      return this.templates.filter(t => t.folder_id === this.folder.id);
    },
    fromName() {
      if (this.previewData && this.previewData.from_name && this.previewData.from_address) {
        return `${this.previewData.from_name} <${this.previewData.from_address}>`
      }
      return this.user.name;
    },
    dataURI() {
      if (this.previewData && this.previewData.content) {
        return htmlService.dataURI(this.previewData.content);
      }
      return null;
    },
  },
  created() {
    if (this.templates.length === 0) {
      this.$store.dispatch('fetchShareTemplates');
    }
  },
  methods: {
    preview(template) {
      this.previewTemplate = template;
      if (template) {
        this.previewData = {
          subject: template.subject,
          content: template.html_body,
          from_address: '',
          from_name: '',
        }
        this.previewDialog = true;
        const form = { // MLD-4920
          preview: true,
          render: false,
        }
        shareTemplateService.render(template.id, form)
          .then(data => {
            this.previewData.from_address = data.from_address;
            this.previewData.from_name = data.from_name;
          })
      } else {
        this.previewData = null;
        this.previewDialog = true;
      }
    },
    select(template) {
      this.$emit('selected', template);
    },
    selectTemplate() { // MLD-4666
      if (this.selectedTemplate) {
        this.select(this.selectedTemplate);
      }
    },
    templateNameSubject(template) { // MLD-4666
      return `${template.name} ${template.subject}`;
    }
  }
}
</script>
